import { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_DURATIONS = {
  danger: 8000,
  warning: 7000,
  success: 6000,
  info: 5000
};

/**
 * Hook de gestion d'une pile de toasts.
 *
 * @param {object} [options]
 * @param {object} [options.durations] - Durées (ms) personnalisées par variant.
 * @returns {{
 *   toasts: Array<{ id: number, message: string, variant: string }>,
 *   showToast: (message: string, variant?: string) => number,
 *   removeToast: (id: number) => void,
 *   clearToasts: () => void,
 *   getDuration: (variant: string) => number
 * }}
 */
export const useToast = (options = {}) => {
  const durations = { ...DEFAULT_DURATIONS, ...(options.durations || {}) };
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const getDuration = useCallback(
    (variant) => durations[variant] ?? durations.info,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [durations.danger, durations.warning, durations.success, durations.info]
  );

  const showToast = useCallback((message, variant = 'success') => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, variant }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => setToasts([]), []);

  useEffect(() => {
    const timers = toasts.map((toast) =>
      setTimeout(() => removeToast(toast.id), getDuration(toast.variant))
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts, getDuration, removeToast]);

  return { toasts, showToast, removeToast, clearToasts, getDuration };
};

export default useToast;
